import React from 'react';
import { Typography } from 'antd';

const { Title, Paragraph } = Typography;

export const TermsContentEN: React.FC = () => {
  return (
    <div className="terms-content">
      <Paragraph className="terms-intro">
        These Terms and Conditions of Use (the "Terms") govern access to and use of the DivvyStash platform 
        (the "Service"). The Service is operated by DivvyStash, a Georgia, United States entity ("DivvyStash," 
        "we," "our," "us").
      </Paragraph>

      <Paragraph className="terms-intro">
        By accessing or using the Service, you ("you," "User") agree to be bound by these Terms. If you do not 
        agree to these Terms, you must not access or use the Service.
      </Paragraph>

      <Title level={2} className="section-title">1. Overview of the Service</Title>

      <Title level={3} className="subsection-title">1.1 Description</Title>
      <Paragraph>
        DivvyStash is an invite-only online platform that allows an administrator ("Admin") to track pooled capital 
        contributions, profit allocations, reinvestment percentages, and withdrawal requests among a closed group of 
        participants ("Contributors" or "Users"). The Service provides recordkeeping and analytics only; it does not 
        execute trades, hold customer funds, or act as a broker, dealer, investment adviser, or custodian.
      </Paragraph>

      <Title level={3} className="subsection-title">1.2 No Brokerage or Custody</Title>
      <Paragraph>
        All underlying investment activity and movement of money occur outside the Service and are managed directly by 
        the Admin or related parties. DivvyStash does not accept deposits, does not process withdrawals of money or 
        crypto assets, and does not control any User's bank, brokerage, or wallet accounts.
      </Paragraph>

      <Title level={3} className="subsection-title">1.3 Relationship Between Users</Title>
      <Paragraph>
        The economic relationship (including any loan, profit-sharing, or investment arrangement) between Admin and 
        Users is separate from the Service and is a private arrangement between those parties. DivvyStash is not a 
        party to those arrangements.
      </Paragraph>

      <Title level={2} className="section-title">2. Eligibility and Accounts</Title>

      <Title level={3} className="subsection-title">2.1 Eligibility</Title>
      <Paragraph>To use the Service, you must:</Paragraph>
      <ul className="terms-list">
        <li>Be at least eighteen (18) years old (or the age of majority in your jurisdiction, if higher); and</li>
        <li>Have been invited to the Service by an Admin or by DivvyStash on an Admin's behalf.</li>
      </ul>

      <Title level={3} className="subsection-title">2.2 Invite-Only Access</Title>
      <Paragraph>
        Users cannot self-register. Account creation is only available via a unique invite link or code sent to the 
        email address designated by the Admin.
      </Paragraph>

      <Title level={3} className="subsection-title">2.3 Account Information</Title>
      <Paragraph>
        You agree to provide accurate, current, and complete information during account registration and to keep that 
        information up to date, including but not limited to:
      </Paragraph>
      <ul className="terms-list">
        <li>Legal name</li>
        <li>Email address</li>
        <li>Country of residence</li>
        <li>Any other information reasonably requested for compliance or tax purposes</li>
      </ul>

      <Title level={3} className="subsection-title">2.4 Account Security</Title>
      <Paragraph>
        You are responsible for maintaining the confidentiality of your login credentials and for all activity that 
        occurs under your account. You agree to notify us promptly of any unauthorized use or suspected compromise.
      </Paragraph>

      <Title level={2} className="section-title">3. Roles: Admin, Contributors, and DivvyStash</Title>

      <Title level={3} className="subsection-title">3.1 Admin</Title>
      <Paragraph>The Admin is the party (or parties) that:</Paragraph>
      <ul className="terms-list">
        <li>Contributes an initial pool of capital (their base principal);</li>
        <li>Invites Users to participate;</li>
        <li>Sets minimum required contributions;</li>
        <li>Records profit amounts in the Service for allocation among Users; and</li>
        <li>Processes withdrawal requests off-platform.</li>
      </ul>

      <Title level={3} className="subsection-title">3.2 Contributors / Users</Title>
      <Paragraph>Contributors are invited Users who:</Paragraph>
      <ul className="terms-list">
        <li>Provide an initial contribution amount, subject to a minimum set by the Admin;</li>
        <li>Have a base principal recorded in the Service;</li>
        <li>Receive a share of recorded profits proportional to their contribution; and</li>
        <li>May choose a reinvestment percentage and submit withdrawal requests through the Service.</li>
      </ul>

      <Title level={3} className="subsection-title">3.3 DivvyStash's Role</Title>
      <Paragraph><strong>DivvyStash provides:</strong></Paragraph>
      <ul className="terms-list">
        <li>The software tools to record contributions, calculate and display profit allocations, and track 
            reinvestment/withdrawable balances;</li>
        <li>A user dashboard and admin interface;</li>
        <li>Certain reporting and summary functionality (e.g., week-to-date, month-to-date, year-to-date, and 
            all-time gains).</li>
      </ul>

      <Paragraph><strong>DivvyStash does not:</strong></Paragraph>
      <ul className="terms-list">
        <li>Perform or execute trades or investment decisions;</li>
        <li>Guarantee or verify the accuracy of profit figures entered by the Admin;</li>
        <li>Guarantee the timing or completion of withdrawals;</li>
        <li>Provide individualized investment, legal, or tax advice.</li>
      </ul>

      <Title level={2} className="section-title">4. Contributions, Base Principal, and Eligibility for Profits</Title>

      <Title level={3} className="subsection-title">4.1 Minimum Contribution</Title>
      <Paragraph>
        For each invited User, the Admin may set a minimum required contribution. The User will not be eligible to 
        receive profit allocations until this minimum has been satisfied and confirmed in the Service.
      </Paragraph>

      <Title level={3} className="subsection-title">4.2 Base Principal</Title>
      <Paragraph>Once a User's initial contribution is confirmed:</Paragraph>
      <ul className="terms-list">
        <li>The User's base principal is recorded in the Service.</li>
        <li>The base principal is treated as locked for purposes of calculating share percentages.</li>
        <li>As between the parties, the economic terms of the contribution (e.g., loan vs. profit-share) are 
            determined by the Admin and User outside the Service.</li>
      </ul>

      <Title level={3} className="subsection-title">4.3 No Ongoing Deposits (MVP Rules)</Title>
      <Paragraph>
        Unless and until the Service explicitly supports additional contributions, no new money may be added by a User 
        after their initial contribution is locked. Any change to this rule must be reflected in updated Terms and/or 
        separate disclosures.
      </Paragraph>

      <Title level={3} className="subsection-title">4.4 Admin Base Principal</Title>
      <Paragraph>
        The Admin may also have a base principal. The Service may adjust the Admin's recorded base principal when new 
        Users contribute, subject to any minimum base the Admin elects to maintain.
      </Paragraph>

      <Title level={2} className="section-title">5. Profit Allocation and Company Fees</Title>

      <Title level={3} className="subsection-title">5.1 Profit Entries</Title>
      <Paragraph>When the Admin records a profit amount (for example, a daily or periodic profit):</Paragraph>
      <ul className="terms-list">
        <li>The Service calculates each User's share based on the User's proportion of the total pool 
            (base principal structure).</li>
        <li>The Service then records net profit for each User after applying any applicable DivvyStash fees 
            and/or Admin-specific rules.</li>
      </ul>

      <Title level={3} className="subsection-title">5.2 Company Fees</Title>
      <Paragraph>
        DivvyStash charges a fixed fee on User profits, structured as a percentage of profits allocated to each User. 
        The fee structure is as follows:
      </Paragraph>
      <ul className="terms-list">
        <li>For Users located in the United States: a fixed 20% fee on allocated profits;</li>
        <li>For Users located outside the United States: a fixed 20% fee on allocated profits.</li>
      </ul>
      <Paragraph>
        Your applicable fee rate is determined by your country designation at account creation and does not change 
        over time. All fees, once assessed, reduce the net profit credited to you. The company may subsidize 
        effective fee rates by keeping company capital invested in the pool alongside user funds.
      </Paragraph>

      <Title level={3} className="subsection-title">5.3 No Guarantee of Profit</Title>
      <Paragraph>
        Profit amounts recorded in the Service represent figures entered by the Admin. DivvyStash does not audit or 
        independently verify such amounts and is not responsible for any discrepancies between recorded profits and 
        underlying investment results.
      </Paragraph>

      <Title level={2} className="section-title">6. Reinvestment and Withdrawable Amounts</Title>

      <Title level={3} className="subsection-title">6.1 Reinvestment Percentage</Title>
      <Paragraph>
        The Service permits each User to set a reinvestment percentage (e.g., 0–100%) that determines how much of 
        their weekly net profit will be added to their base principal.
      </Paragraph>

      <Title level={3} className="subsection-title">6.2 Weekly Settlements</Title>
      <Paragraph>
        On a defined weekly schedule (e.g., Saturday at 23:59 UTC) or at intervals determined by the Admin:
      </Paragraph>
      <ul className="terms-list">
        <li>The Service calculates the User's profit for the week (net of company fees).</li>
        <li>A portion equal to the User's reinvestment percentage is added to the User's base principal.</li>
        <li>The remainder is moved into a withdrawable amount balance.</li>
        <li>The "profit for the week" counter is reset in the Service.</li>
      </ul>

      <Title level={3} className="subsection-title">6.3 Effect of Reinvestment</Title>
      <Paragraph>
        Reinvested amounts increase the User's base principal for purposes of future allocation calculations. 
        Reinvestment does not change the historical contribution baseline for tax or accounting purposes; those 
        relationships are defined outside the Service.
      </Paragraph>

      <Title level={2} className="section-title">7. Withdrawals</Title>

      <Title level={3} className="subsection-title">7.1 Withdrawal Requests</Title>
      <Paragraph>
        Users may request withdrawals of their withdrawable balance through the Service. A withdrawal request is a 
        signal to the Admin and does not, by itself, move any funds.
      </Paragraph>

      <Title level={3} className="subsection-title">7.2 Admin Processing</Title>
      <Paragraph>The Admin is solely responsible for:</Paragraph>
      <ul className="terms-list">
        <li>Reviewing and either approving, rejecting, or marking requests as completed; and</li>
        <li>Initiating and completing the actual payout (e.g., bank transfer, wallet transfer) outside the Service.</li>
      </ul>

      <Title level={3} className="subsection-title">7.3 Timing and Delays</Title>
      <Paragraph>DivvyStash does not control, and is not responsible for, any delays or failures in:</Paragraph>
      <ul className="terms-list">
        <li>Payment processing by banks, payment providers, or blockchain networks;</li>
        <li>The Admin's own responsiveness or actions.</li>
      </ul>

      <Title level={3} className="subsection-title">7.4 No Custody</Title>
      <Paragraph>
        DivvyStash does not hold or control User funds and cannot issue or reverse payouts on your behalf.
      </Paragraph>

      <Title level={2} className="section-title">8. Investment Risk; No Advice</Title>

      <Title level={3} className="subsection-title">8.1 Risk of Loss</Title>
      <Paragraph>You acknowledge and agree that:</Paragraph>
      <ul className="terms-list">
        <li>All investments involve risk, including the possible loss of some or all of the capital you have contributed;</li>
        <li>There is no guarantee of profit, return, or protection of principal; and</li>
        <li>Past performance of any investment strategy or Admin does not guarantee future results.</li>
      </ul>

      <Title level={3} className="subsection-title">8.2 No Investment Advice</Title>
      <Paragraph>
        The Service and any content provided through it are for informational and administrative purposes only. 
        Nothing in or through the Service constitutes:
      </Paragraph>
      <ul className="terms-list">
        <li>Investment advice;</li>
        <li>A recommendation to buy, sell, or hold any security, digital asset, or other instrument; or</li>
        <li>Legal, tax, or accounting advice.</li>
      </ul>

      <Title level={3} className="subsection-title">8.3 Independent Decisions</Title>
      <Paragraph>
        You are solely responsible for any decisions you make regarding participation, contributions, withdrawals, or 
        reliance on performance information shown in the Service. You should consult your own financial, legal, and 
        tax advisers.
      </Paragraph>

      <Title level={2} className="section-title">9. Taxes and Withholding</Title>

      <Title level={3} className="subsection-title">9.1 User Responsibility</Title>
      <Paragraph>You are solely responsible for:</Paragraph>
      <ul className="terms-list">
        <li>Determining your own tax obligations arising from participation in any arrangement tracked by the Service; and</li>
        <li>Filing all required tax returns and paying any applicable taxes in your jurisdiction.</li>
      </ul>

      <Title level={3} className="subsection-title">9.2 No Tax Advice</Title>
      <Paragraph>
        DivvyStash does not provide tax advice or withhold taxes from distributions. Any financial summaries or 
        labels in the Service are informational only and do not constitute tax advice.
      </Paragraph>

      <Title level={2} className="section-title">10. User Obligations and Prohibited Conduct</Title>

      <Title level={3} className="subsection-title">10.1 Your Obligations</Title>
      <Paragraph>You agree to:</Paragraph>
      <ul className="terms-list">
        <li>Use the Service only in accordance with these Terms and applicable law;</li>
        <li>Provide accurate and truthful information and keep it current;</li>
        <li>Maintain the confidentiality of your account credentials;</li>
        <li>Notify us promptly of any unauthorized access or security incident involving your account.</li>
      </ul>

      <Title level={3} className="subsection-title">10.2 Prohibited Uses</Title>
      <Paragraph>You agree not to:</Paragraph>
      <ul className="terms-list">
        <li>Use the Service for any unlawful purpose, including fraud, money laundering, or terrorist financing;</li>
        <li>Impersonate any person or entity, or misrepresent your affiliation;</li>
        <li>Interfere with or disrupt the operation of the Service, including by introducing viruses, malware, or 
            other harmful code;</li>
        <li>Attempt to gain unauthorized access to other accounts or data;</li>
        <li>Use any automated means (such as bots or scrapers) to access or collect information from the Service, 
            except as expressly permitted;</li>
        <li>Circumvent or attempt to circumvent any security or access-control measures.</li>
      </ul>
      <Paragraph>
        DivvyStash reserves the right to investigate and, where appropriate, report any suspected misuse to law 
        enforcement or other authorities.
      </Paragraph>

      <Title level={2} className="section-title">11. Privacy and Data</Title>

      <Title level={3} className="subsection-title">11.1 Privacy Policy</Title>
      <Paragraph>
        Your use of the Service is also governed by our Privacy Policy, which describes how we collect, use, and 
        share information about you. The Privacy Policy is incorporated into these Terms by reference.
      </Paragraph>

      <Title level={3} className="subsection-title">11.2 Data Collected</Title>
      <Paragraph>Without limiting the Privacy Policy, the Service may collect and store:</Paragraph>
      <ul className="terms-list">
        <li>Account and profile information;</li>
        <li>Contribution and profit allocation records;</li>
        <li>Withdrawal requests and status;</li>
        <li>Technical and usage data (including logs, device data, and approximate location).</li>
      </ul>

      <Title level={3} className="subsection-title">11.3 Security</Title>
      <Paragraph>
        We implement reasonable technical and organizational measures designed to protect your information. However, 
        no system is completely secure, and we cannot guarantee that unauthorized third parties will never defeat our 
        security measures.
      </Paragraph>

      <Title level={2} className="section-title">12. Intellectual Property</Title>

      <Title level={3} className="subsection-title">12.1 Ownership</Title>
      <Paragraph>DivvyStash and its licensors own all right, title, and interest in and to the Service, including:</Paragraph>
      <ul className="terms-list">
        <li>The DivvyStash name and logo;</li>
        <li>All software, code, text, graphics, and other content; and</li>
        <li>All associated intellectual property rights.</li>
      </ul>

      <Title level={3} className="subsection-title">12.2 Limited License</Title>
      <Paragraph>
        Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, 
        revocable license to access and use the Service for your personal or internal business purposes.
      </Paragraph>

      <Title level={3} className="subsection-title">12.3 Restrictions</Title>
      <Paragraph>You may not:</Paragraph>
      <ul className="terms-list">
        <li>Copy, modify, distribute, sell, or lease any part of the Service;</li>
        <li>Reverse engineer or attempt to extract the source code, except to the extent such restrictions are 
            prohibited by law;</li>
        <li>Remove, obscure, or alter any proprietary notices.</li>
      </ul>

      <Title level={2} className="section-title">13. Service Availability, Changes, and Updates</Title>

      <Title level={3} className="subsection-title">13.1 No Guarantee of Availability</Title>
      <Paragraph>
        We do not guarantee that the Service will be available at all times or without interruption. Maintenance, 
        technical issues, or other events may result in downtime.
      </Paragraph>

      <Title level={3} className="subsection-title">13.2 Changes to the Service</Title>
      <Paragraph>
        We may modify, enhance, or discontinue any part of the Service at any time, with or without notice, provided 
        that such changes do not materially violate these Terms.
      </Paragraph>

      <Title level={3} className="subsection-title">13.3 Beta Features</Title>
      <Paragraph>
        We may offer new or experimental features designated as "beta" or similar. Such features may be modified or 
        discontinued at our discretion and are provided "as is" without any warranties.
      </Paragraph>

      <Title level={2} className="section-title">14. Suspension and Termination</Title>

      <Title level={3} className="subsection-title">14.1 Suspension or Termination by DivvyStash</Title>
      <Paragraph>We may suspend or terminate your access to the Service, in whole or in part, if:</Paragraph>
      <ul className="terms-list">
        <li>You violate these Terms;</li>
        <li>We suspect fraudulent or unlawful activity;</li>
        <li>We are required to do so by law or regulatory request; or</li>
        <li>Continuing to provide the Service to you would create undue risk for us.</li>
      </ul>

      <Title level={3} className="subsection-title">14.2 Effect of Termination</Title>
      <Paragraph>Upon termination:</Paragraph>
      <ul className="terms-list">
        <li>Your right to access the Service will cease;</li>
        <li>We may retain records as required by law or for legitimate business purposes;</li>
        <li>Any economic rights or obligations between you and the Admin (e.g., outstanding balances) remain between 
            you and the Admin and are not extinguished by your loss of access to the Service.</li>
      </ul>

      <Title level={3} className="subsection-title">14.3 Voluntary Closure</Title>
      <Paragraph>
        You may request closure of your account at any time by contacting us. We may retain certain data to comply 
        with legal obligations or for legitimate recordkeeping.
      </Paragraph>

      <Title level={2} className="section-title">15. Disclaimers</Title>

      <Title level={3} className="subsection-title">15.1 "As Is" and "As Available"</Title>
      <Paragraph>
        The Service is provided on an "as is" and "as available" basis without warranties of any kind, whether 
        express, implied, or statutory, including any implied warranties of merchantability, fitness for a particular 
        purpose, title, and non-infringement.
      </Paragraph>

      <Title level={3} className="subsection-title">15.2 Accuracy of Information</Title>
      <Paragraph>
        While we strive to provide accurate and up-to-date information, we do not warrant that any data, analytics, 
        or summaries made available through the Service are accurate, complete, or error-free. Profit entries and 
        many other data points are provided by the Admin and may not be independently verified.
      </Paragraph>

      <Title level={3} className="subsection-title">15.3 Third-Party Services</Title>
      <Paragraph>
        The Service may rely on third-party providers (e.g., hosting, email, analytics). We are not responsible for 
        the acts, omissions, or availability of such third parties.
      </Paragraph>

      <Title level={2} className="section-title">16. Limitation of Liability</Title>

      <Paragraph>To the maximum extent permitted by law:</Paragraph>

      <Title level={3} className="subsection-title">16.1 No Indirect Damages</Title>
      <Paragraph>
        DivvyStash and its affiliates, officers, directors, employees, and agents will not be liable for any indirect, 
        incidental, consequential, special, punitive, or exemplary damages, including lost profits, loss of data, or 
        business interruption, arising from or related to your use of or inability to use the Service.
      </Paragraph>

      <Title level={3} className="subsection-title">16.2 Liability Cap</Title>
      <Paragraph>
        DivvyStash's total aggregate liability arising out of or related to these Terms or your use of the Service 
        will not exceed the greater of:
      </Paragraph>
      <ul className="terms-list">
        <li>The total amount of fees, if any, paid by you to DivvyStash for use of the Service during the twelve 
            (12) months preceding the event giving rise to the claim; or</li>
        <li>USD $1,000.</li>
      </ul>

      <Title level={3} className="subsection-title">16.3 Applicability</Title>
      <Paragraph>
        Some jurisdictions do not allow the exclusion or limitation of certain damages, so the above limitations may 
        not apply to you. In such cases, our liability will be limited to the maximum extent permitted by law.
      </Paragraph>

      <Title level={2} className="section-title">17. Indemnification</Title>

      <Paragraph>
        You agree to indemnify, defend, and hold harmless DivvyStash and its affiliates, officers, directors, 
        employees, and agents from and against any and all claims, liabilities, damages, losses, and expenses 
        (including reasonable attorneys' fees) arising out of or related to:
      </Paragraph>
      <ul className="terms-list">
        <li>Your use of the Service;</li>
        <li>Your violation of these Terms;</li>
        <li>Your violation of any rights of another person or entity; or</li>
        <li>Any dispute between you and the Admin or any other User regarding contributions, profits, or withdrawals.</li>
      </ul>

      <Title level={2} className="section-title">18. Governing Law and Dispute Resolution</Title>

      <Title level={3} className="subsection-title">18.1 Governing Law</Title>
      <Paragraph>
        These Terms and any dispute arising out of or relating to them or the Service will be governed by and 
        construed in accordance with the laws of the State of Georgia, without regard to its conflict of laws principles.
      </Paragraph>

      <Title level={3} className="subsection-title">18.2 Dispute Resolution; Arbitration</Title>
      <Paragraph>
        Any dispute, claim, or controversy arising out of or relating to these Terms or the Service that cannot be 
        resolved informally will be finally resolved by binding arbitration administered by American Arbitration 
        Association (AAA) under its applicable rules. The arbitration will be conducted in Suwanee, Georgia in the 
        English language, and the arbitrator's decision will be final and binding.
      </Paragraph>
      <Paragraph>
        You and DivvyStash agree to resolve disputes on an individual basis, and not as part of any class or 
        representative action, to the maximum extent permitted by law.
      </Paragraph>

      <Title level={2} className="section-title">19. Changes to These Terms</Title>

      <Paragraph>
        We may update these Terms from time to time. When we do, we will revise the "Last updated" date at the top 
        of this document and may provide additional notice (such as via email or in-app notification) where required 
        by law.
      </Paragraph>

      <Paragraph>
        If you continue to use the Service after the updated Terms become effective, you are deemed to have accepted 
        the changes. If you do not agree to the updated Terms, you must stop using the Service.
      </Paragraph>

      <Title level={2} className="section-title">20. Miscellaneous</Title>

      <Title level={3} className="subsection-title">20.1 Entire Agreement</Title>
      <Paragraph>
        These Terms, together with any policies or additional terms referred to herein (including any applicable 
        Privacy Policy), constitute the entire agreement between you and DivvyStash regarding the Service.
      </Paragraph>

      <Title level={3} className="subsection-title">20.2 Severability</Title>
      <Paragraph>
        If any provision of these Terms is held to be invalid or unenforceable, the remaining provisions will remain 
        in full force and effect.
      </Paragraph>

      <Title level={3} className="subsection-title">20.3 No Waiver</Title>
      <Paragraph>
        Our failure to enforce any right or provision of these Terms will not constitute a waiver of such right or 
        provision.
      </Paragraph>

      <Title level={3} className="subsection-title">20.4 Assignment</Title>
      <Paragraph>
        You may not assign or transfer your rights or obligations under these Terms without our prior written consent. 
        We may assign these Terms without restriction.
      </Paragraph>

      <Title level={3} className="subsection-title">20.5 Contact</Title>
      <Paragraph>
        If you have questions about these Terms or the Service, please contact us at:
      </Paragraph>
      <Paragraph style={{ marginLeft: 24 }}>
        <strong>DivvyStash</strong><br />
        Email: <a href="mailto:support@divvystash.com">support@divvystash.com</a><br />
        Address: 2320 Prosperity Way, Suwanee, GA 30024
      </Paragraph>
    </div>
  );
};
